import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("aurelo_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
      setTenants(data.tenants || []);
    } catch (e) {
      localStorage.removeItem("aurelo_token");
      localStorage.removeItem("aurelo_tenant");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const persistSession = (data) => {
    localStorage.setItem("aurelo_token", data.token);
    if (data.tenant_id) localStorage.setItem("aurelo_tenant", data.tenant_id);
    setUser(data.user);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persistSession(data);
    await loadMe();
    return data;
  };

  const googleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    persistSession(data);
    await loadMe();
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    persistSession(data);
    await loadMe();
    return data;
  };

  const acceptInvite = async (token, password) => {
    const { data } = await api.post("/auth/accept-invite", { token, password });
    persistSession(data);
    await loadMe();
    return data;
  };

  const switchTenant = async (tenantId) => {
    localStorage.setItem("aurelo_tenant", tenantId);
    await loadMe();
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/auth/me", payload);
    setUser(data.user);
    return data.user;
  };

  const changePassword = async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password });
  };

  const logout = () => {
    localStorage.removeItem("aurelo_token");
    localStorage.removeItem("aurelo_tenant");
    setUser(null); setTenant(null); setTenants([]);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, tenant, tenants, loading, login, googleSession, signup, acceptInvite, logout, switchTenant, updateProfile, changePassword, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
